// Whether this browser is signed in, and the transport's reaction to a
// session that ended. The credential itself lives in lib/auth.ts.

import { createContext, useCallback, useContext, useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { configureClient } from '../api/client';
import { getTokens, logout, subscribe } from './auth';

interface Session {
  signedIn: boolean;
  signOut: () => void;
}

const Ctx = createContext<Session | null>(null);

// At import, so the transport is pointed at the API before any screen's first
// query: a child's effect runs before its provider's.
configureClient(
  () => {},
  () => {},
);

export function SessionProvider({ children }: { children: ReactNode }) {
  const tokens = useSyncExternalStore(subscribe, getTokens);
  const qc = useQueryClient();

  const signOut = useCallback(() => {
    qc.clear();
    void logout();
  }, [qc]);

  // A renewed token means whatever was refused can be asked for again; no
  // credential left means the sign-in page, and nothing cached from the
  // account that was signed in.
  useEffect(() => {
    configureClient(
      () => void qc.invalidateQueries(),
      () => qc.clear(),
    );
  }, [qc]);

  const value = useMemo(() => ({ signedIn: !!tokens, signOut }), [tokens, signOut]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error('useSession outside SessionProvider');
  return s;
}
